import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Box } from '@mui/material';

ChartJS.register(...registerables);

const ActiveUsersStatusBox = ({
    activeUsers = 78.4,
    inactiveUsers = 21.6,
    activePercentage,
    inactivePercentage
}) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const hoveredIndexRef = useRef(null);

    // Calculate percentages from counts if not directly provided
    const total = activeUsers + inactiveUsers;
    const activePercent = activePercentage
        ? parseFloat(activePercentage)
        : total > 0 ? Math.round((activeUsers / total) * 100 * 10) / 10 : 0;
    const inactivePercent = inactivePercentage
        ? parseFloat(inactivePercentage)
        : total > 0 ? Math.round((inactiveUsers / total) * 100 * 10) / 10 : 0;

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');

            const calloutPlugin = {
                id: 'calloutPlugin',
                afterDraw(chart) {
                    const { ctx, data } = chart;
                    const percentages = [inactivePercent, activePercent];

                    chart.getDatasetMeta(0).data.forEach((datapoint, index) => {
                        const { x, y, outerRadius, startAngle, endAngle } = datapoint;
                        const midAngle = (startAngle + endAngle) / 2;

                        // Check if this segment is being hovered
                        const isHovered = hoveredIndexRef.current === index;

                        // Indices: 0 for Inactive (Grey), 1 for Active (Blue)
                        const accentColor = index === 1 ? '#007bff' : '#999999';

                        // Draw dot on the arc
                        const dotRadius = isHovered ? 4 : 3; // Bigger dot on hover
                        const dotX = x + Math.cos(midAngle) * outerRadius;
                        const dotY = y + Math.sin(midAngle) * outerRadius;

                        ctx.beginPath();
                        ctx.arc(dotX, dotY, dotRadius, 0, 2 * Math.PI);
                        ctx.fillStyle = accentColor;
                        ctx.fill();

                        // Draw callout line
                        const lineStartX = x + Math.cos(midAngle) * outerRadius;
                        const lineStartY = y + Math.sin(midAngle) * outerRadius;

                        // Extension length - slightly longer on hover
                        const extension = isHovered ? 28 : 25;
                        const lineMidX = x + Math.cos(midAngle) * (outerRadius + extension);
                        const lineMidY = y + Math.sin(midAngle) * (outerRadius + extension);

                        const isRight = Math.cos(midAngle) > 0;
                        const hLineLength = isHovered ? 24 : 20;
                        const lineEndX = lineMidX + (isRight ? hLineLength : -hLineLength);
                        const lineEndY = lineMidY;

                        ctx.beginPath();
                        ctx.moveTo(lineStartX, lineStartY);
                        ctx.lineTo(lineMidX, lineMidY);
                        ctx.lineTo(lineEndX, lineEndY);
                        ctx.strokeStyle = accentColor;
                        ctx.lineWidth = isHovered ? 2 : 1.5; // Thicker line on hover
                        ctx.stroke();

                        // Draw percentage label with animation
                        const label = percentages[index] + '%';
                        const fontSize = isHovered ? 24 : 20; // Bigger font on hover
                        const fontWeight = isHovered ? 700 : 600; // Bolder on hover
                        ctx.font = `${fontWeight} ${fontSize}px "Inter", sans-serif`;
                        ctx.fillStyle = accentColor;
                        ctx.textAlign = isRight ? 'left' : 'right';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(label, lineEndX + (isRight ? 8 : -8), lineEndY);
                    });
                }
            };

            chartInstance.current = new ChartJS(ctx, {
                type: 'doughnut',
                data: {
                    labels: ['Inactive', 'Active'],
                    datasets: [
                        {
                            data: [inactiveUsers, activeUsers],
                            backgroundColor: ['#999999', '#007bff'],
                            hoverBackgroundColor: ['#808080', '#0056b3'],
                            borderWidth: 0,
                            cutout: '70%',
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    rotation: 180,
                    layout: {
                        padding: 50, // Increased padding for hover animation
                    },
                    onHover: (event, activeElements) => {
                        // Update hovered index
                        if (activeElements.length > 0) {
                            hoveredIndexRef.current = activeElements[0].index;
                        } else {
                            hoveredIndexRef.current = null;
                        }
                        // Trigger redraw to show animation
                        if (chartInstance.current) {
                            chartInstance.current.update('none');
                        }
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            callbacks: {
                                label: (context) => {
                                    const label = context.label || '';
                                    const value = context.raw || 0;
                                    const percentage = context.dataIndex === 0 ? inactivePercent : activePercent;
                                    return `${label}: ${value} (${percentage}%)`;
                                }
                            }
                        },
                        datalabels: {
                            display: false
                        }
                    },
                },
                plugins: [calloutPlugin],
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [activeUsers, inactiveUsers, activePercent, inactivePercent]);

    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%', flex: 1 }}>
            <canvas ref={chartRef} style={{ width: '100%', height: '100%', cursor: 'pointer' }} />
        </Box>
    );
};

export default ActiveUsersStatusBox;
